import { getSpeechRecognitionLocale } from "./translations";
import { stripMarkdownLinks } from "./markdownLinkParse";

const stripTextForSpeech = (text: string): string =>
  stripMarkdownLinks(String(text).replaceAll(/[*_#`>]/g, ""))
    .replaceAll(/\n+/g, ". ")
    .replaceAll(/\s{2,}/g, " ")
    .trim();

let voicesReadyPromise: Promise<void> | null = null;

const wait = (ms: number) =>
  new Promise<void>((resolve) => {
    globalThis.setTimeout(resolve, ms);
  });

export const loadTaraSpeechVoices = (): Promise<void> => {
  const synth = globalThis.speechSynthesis;
  if (!synth) return Promise.resolve();
  if (synth.getVoices().length > 0) return Promise.resolve();

  if (voicesReadyPromise) return voicesReadyPromise;

  voicesReadyPromise = new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      synth.onvoiceschanged = null;
      resolve();
    };

    synth.onvoiceschanged = finish;
    globalThis.setTimeout(finish, 1200);
  });

  return voicesReadyPromise;
};

const pickVoice = (languageCode: string): SpeechSynthesisVoice | null => {
  const synth = globalThis.speechSynthesis;
  if (!synth) return null;

  const voices = synth.getVoices();
  if (!voices.length) return null;

  const locale = getSpeechRecognitionLocale(languageCode);
  const langPrefix = locale.split("-")[0];

  return (
    voices.find((voice) => voice.lang === locale) ||
    voices.find((voice) => voice.lang.startsWith(langPrefix)) ||
    voices.find((voice) => voice.lang.startsWith("en")) ||
    voices[0]
  );
};

/** Call on user tap so Chrome/Edge allow later async speech. */
export const primeTaraSpeech = (): void => {
  const synth = globalThis.speechSynthesis;
  if (!synth) return;

  loadTaraSpeechVoices().catch(() => {});

  const unlock = new SpeechSynthesisUtterance("\u200B");
  unlock.volume = 0.01;
  unlock.rate = 2;
  synth.speak(unlock);
};

let speakSessionId = 0;

export const stopTaraSpeech = (invalidateSession = true): void => {
  if (invalidateSession) {
    speakSessionId += 1;
  }
  globalThis.speechSynthesis?.cancel();
};

const isSynthBusy = (): boolean => {
  const synth = globalThis.speechSynthesis;
  return Boolean(synth?.speaking || synth?.pending);
};

type SpeakOptions = {
  cancelPrevious?: boolean;
};

export const speakTaraResponse = async (
  text: string,
  languageCode: string,
  { cancelPrevious = true }: SpeakOptions = {}
): Promise<boolean> => {
  const synth = globalThis.speechSynthesis;
  const cleaned = stripTextForSpeech(text);

  if (!synth || !cleaned) {
    return false;
  }

  await loadTaraSpeechVoices();

  if (cancelPrevious) {
    stopTaraSpeech(false);
    await wait(20);
  }

  const sessionAtStart = speakSessionId;

  return new Promise((resolve) => {
    if (sessionAtStart !== speakSessionId) {
      resolve(false);
      return;
    }

    const utterance = new SpeechSynthesisUtterance(cleaned);
    utterance.lang = getSpeechRecognitionLocale(languageCode);
    utterance.rate = 1.18;
    utterance.pitch = 1.05;
    utterance.volume = 1;

    const voice = pickVoice(languageCode);
    if (voice) {
      utterance.voice = voice;
    }

    let settled = false;
    let speechStarted = false;

    const finish = (success: boolean) => {
      if (settled || sessionAtStart !== speakSessionId) return;
      settled = true;
      if (resumeTimer) {
        globalThis.clearInterval(resumeTimer);
      }
      if (startTimer) {
        globalThis.clearTimeout(startTimer);
      }
      resolve(success);
    };

    const resumeTimer = globalThis.setInterval(() => {
      if (!synth.speaking) return;
      synth.pause();
      synth.resume();
    }, 8000);

    utterance.onstart = () => {
      speechStarted = true;
      globalThis.clearTimeout(startTimer);
    };

    utterance.onend = () => {
      finish(speechStarted);
    };

    utterance.onerror = () => {
      finish(false);
    };

    synth.speak(utterance);

    const startTimer = globalThis.setTimeout(() => {
      if (settled) return;
      if (speechStarted || isSynthBusy()) {
        return;
      }
      finish(false);
    }, 2200);
  });
};

const isRetrySessionActive = (sessionAtStart: number) => sessionAtStart === speakSessionId;

const runSpeakAttempt = async (
  text: string,
  languageCode: string,
  attempt: number
) =>
  speakTaraResponse(text, languageCode, {
    cancelPrevious: attempt === 0,
  });

export const speakTaraResponseWithRetry = async (
  text: string,
  languageCode: string,
  attempts = 2,
  delayMs = 250
): Promise<boolean> => {
  const sessionAtStart = speakSessionId;

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    if (!isRetrySessionActive(sessionAtStart)) {
      return false;
    }

    await loadTaraSpeechVoices();

    if (attempt > 0) {
      if (isSynthBusy()) {
        return true;
      }
      await wait(delayMs);
      if (!isRetrySessionActive(sessionAtStart)) {
        return false;
      }
      if (isSynthBusy()) {
        return true;
      }
    }

    const spoke = await runSpeakAttempt(text, languageCode, attempt);
    if (spoke || isSynthBusy()) {
      return true;
    }
  }

  return false;
};
